import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  School,
  CalendarMonth,
  Category as CategoryIcon,
  CurrencyRupee,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { courseAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../../components/ConfirmDialog';

const CourseList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, user } = useAuth();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getAll();
      const allCourses = response.data.data || [];

      // Filter courses for branch admin - only show their assigned courses
      if (!isSuperAdmin && user?.branch?.courses) {
        const branchCourseIds = user.branch.courses.map(c => c._id || c);
        const filteredCourses = allCourses.filter(course =>
          branchCourseIds.includes(course._id)
        );
        setCourses(filteredCourses);
      } else {
        setCourses(allCourses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, user]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleToggleBlock = (courseId, currentStatus, courseName) => {
    const action = currentStatus ? 'block' : 'activate';
    setConfirmDialog({
      open: true,
      title: `${action === 'block' ? 'Block' : 'Activate'} Course`,
      message: `Are you sure you want to ${action} "${courseName}"?`,
      onConfirm: async () => {
        try {
          await courseAPI.update(courseId, { isActive: !currentStatus });
          toast.success(`Course ${action}ed successfully`);
          fetchCourses();
        } catch (error) {
          console.error('Error updating course:', error);
          toast.error(error.response?.data?.message || 'Failed to update course');
        }
      },
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isSuperAdmin ? 'Courses' : 'My Branch Courses'}
        </Typography>
        {isSuperAdmin && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/courses/categories')}
            >
              Manage Categories
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/admin/courses/create')}
            >
              Create Course
            </Button>
          </Box>
        )}
      </Box>

      {courses.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info">
            No courses found. Create categories first, then add courses.
          </Alert>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {courses.map((course) => (
            <Grid item xs={12} md={6} lg={4} key={course._id}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {course.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Code: {course.code}
                      </Typography>
                    </Box>
                    <Chip
                      label={course.isActive ? 'ACTIVE' : 'INACTIVE'}
                      color={course.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    {course.category && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {course.category.name || 'Uncategorized'}
                        </Typography>
                      </Box>
                    )}

                    {course.duration && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonth fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {course.duration.value} {course.duration.unit}
                        </Typography>
                      </Box>
                    )}

                    {course.numberOfSemesters > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <School fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {course.numberOfSemesters} Semester{course.numberOfSemesters !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}

                    {course.semesters && course.semesters.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                          {course.semesters.reduce((total, sem) => total + (sem.subjects?.length || 0), 0)} Total Subjects
                        </Typography>
                      </Box>
                    )}

                    {course.fees && course.fees.amount > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CurrencyRupee fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          ₹{course.fees.amount.toLocaleString()}
                        </Typography>
                      </Box>
                    )}

                    {course.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {course.description.length > 100
                          ? `${course.description.substring(0, 100)}...`
                          : course.description}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>

                {isSuperAdmin && (
                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    <Tooltip title="Edit Course">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/admin/courses/edit/${course._id}`)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={course.isActive ? 'Block Course' : 'Activate Course'}>
                      <IconButton
                        color={course.isActive ? 'error' : 'success'}
                        size="small"
                        onClick={() => handleToggleBlock(course._id, course.isActive, course.name)}
                      >
                        {course.isActive ? <Block /> : <CheckCircle />}
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmColor="primary"
      />
    </Box>
  );
};

export default CourseList;
